import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Pencil, Plus, Check, X, CalendarIcon } from "lucide-react";
import type { AdminUserItem } from "@shared-core/data/adapter";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { paths } from "@shared-core/routing/routes";
import {
  SELECTABLE_MARKETING_CATEGORIES,
  isSelectableMarketingCategory,
  type LeadSource,
  type SelectableMarketingCategory,
} from "@shared-core/data/marketing-types";
import type * as API from "@shared-core/data/api-types";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Calendar } from "../app/components/primitives/calendar";
import { Input } from "../app/components/primitives/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../app/components/primitives/popover";
import { Switch } from "../app/components/primitives/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Toggle } from "../app/components/primitives/toggle";

const TABLE_COLUMN_COUNT = 9;

/**
 * "Which landing pages exist" list, plus create/edit — the confirmed
 * follow-up after Big Pink Book proved the pattern (landing-page-lead-capture
 * product brief §5's deferred admin UI). The slug is immutable once created
 * (it's baked into the live WordPress form's URL, and the backend's
 * UpdateLeadSourceRequest has no slug field), so only name/active state/
 * categories/launch date are editable after creation.
 *
 * Categories and launch date are edit-only, not set at creation time — a
 * source is typically created here before the page itself is built in
 * WordPress, so neither is usually known yet (see the "Launched" column).
 */
export function LandingPagesPage() {
  const dataAdapter = useDataAdapter();
  const [sources, setSources] = useState<LeadSource[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  const [isSaving, setIsSaving] = useState(false);

  const [categoriesPopoverId, setCategoriesPopoverId] = useState<string | null>(
    null,
  );
  const [pendingCategories, setPendingCategories] = useState<
    SelectableMarketingCategory[]
  >([]);

  const [launchPopoverId, setLaunchPopoverId] = useState<string | null>(null);

  // The CRM categories a routed ticket can be filed under. Fetched rather
  // than hardcoded: they are rows a manager maintains under Settings, not a
  // fixed enum like the product categories beside them.
  const [ticketCategories, setTicketCategories] = useState<
    API.TicketCategory[]
  >([]);
  const [ticketCategoryPopoverId, setTicketCategoryPopoverId] = useState<
    string | null
  >(null);

  // Sales reps a campaign's unownable enquiries can fall back to. Fetched
  // rather than hardcoded, and filtered to people who have actually signed
  // in — an account that has never logged in cannot work a lead, and naming
  // one is how these enquiries came to be owned by nobody in the first place.
  const [salesReps, setSalesReps] = useState<AdminUserItem[]>([]);
  const [defaultRepPopoverId, setDefaultRepPopoverId] = useState<string | null>(
    null,
  );

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await dataAdapter.listLeadSources();
      setSources(res.items);
    } catch (e) {
      console.error("marketing.landing_pages.load.failed", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Active only — a retired category is not a valid destination for new
    // tickets, and offering one would quietly route them somewhere nobody
    // is looking.
    dataAdapter
      .getAdminUsers({ role: "sales_rep", pageSize: 200 })
      .then((res) => setSalesReps(res.items.filter((u) => u.has_logged_in)))
      .catch((e) => {
        // Non-fatal: the rest of the page works, the picker just has nothing
        // to offer. Failing the page over it would be worse.
        console.error("marketing.landing_pages.sales_reps.failed", e);
      });
    dataAdapter
      .listTicketCategories(true)
      .then(setTicketCategories)
      .catch((e) => {
        // Non-fatal: the rest of the page still works, the picker just has
        // nothing to offer. Failing the whole page over it would be worse.
        console.error("marketing.landing_pages.ticket_categories.failed", e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAdapter]);

  const handleCreate = async () => {
    const name = newName.trim();
    const slug = newSlug.trim();
    if (!name || !slug) return;
    setIsSaving(true);
    setCreateError(null);
    try {
      await dataAdapter.createLeadSource({ name, slug });
      setNewName("");
      setNewSlug("");
      setIsCreating(false);
      await load();
    } catch (e) {
      console.error("marketing.landing_pages.create.failed", e);
      setCreateError(
        "Couldn't create that landing page — the slug may already be in use.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEdit = (source: LeadSource) => {
    setEditingId(source.id);
    setEditingName(source.name);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const name = editingName.trim();
    if (!name) return;
    setIsSaving(true);
    try {
      await dataAdapter.updateLeadSource(editingId, { name });
      setEditingId(null);
      await load();
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (source: LeadSource) => {
    await dataAdapter.setLeadSourceActive(source.id, !source.is_active);
    await load();
  };

  const handleOpenCategoriesPopover = (source: LeadSource, open: boolean) => {
    if (open) {
      // A stored "other" (never offered here) is not a scope the request takes.
      setPendingCategories(
        (source.marketing_categories ?? []).filter(
          isSelectableMarketingCategory,
        ),
      );
      setCategoriesPopoverId(source.id);
      return;
    }
    // Closing commits whatever was toggled — a single save per edit
    // session, not one per checkbox click.
    setCategoriesPopoverId(null);
    void dataAdapter
      .updateLeadSource(source.id, {
        marketing_categories:
          pendingCategories.length > 0 ? pendingCategories : null,
      })
      .then(load);
  };

  const handleSetTicketCategory = async (
    source: LeadSource,
    categoryId: string | undefined,
  ) => {
    setTicketCategoryPopoverId(null);
    await dataAdapter.updateLeadSource(source.id, {
      ticket_category_id: categoryId ?? null,
    });
    await load();
  };

  const handleSetDefaultRep = async (
    source: LeadSource,
    userId: string | undefined,
  ) => {
    setDefaultRepPopoverId(null);
    await dataAdapter.updateLeadSource(source.id, {
      default_sales_rep_user_id: userId ?? null,
    });
    await load();
  };

  const handleSetLaunchedAt = async (
    source: LeadSource,
    date: Date | undefined,
  ) => {
    setLaunchPopoverId(null);
    await dataAdapter.updateLeadSource(source.id, {
      launched_at: date ? date.toISOString() : null,
    });
    await load();
  };

  return (
    <div className="space-y-6">
      <MarketingNav active="landing-pages" />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl mb-1">Landing pages</h1>
          <p className="text-sm text-muted-foreground">
            Every landing page currently wired up to accept form submissions.
            The slug can't be changed once created — it's baked into the live
            page's integration.
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="size-4 mr-2" />
          New landing page
        </Button>
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground">
          Loading landing pages…
        </div>
      )}

      {error && !loading && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
          Couldn't load landing pages. Please try again.
        </div>
      )}

      {!loading && !error && sources && (
        <div className="rounded-lg border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                {/* "Categories" alone was ambiguous the moment a second
                    kind of category appeared in this table. The cell's own
                    aria-label and popover copy already said "product
                    categories" — the header was the odd one out. */}
                <TableHead>Product categories</TableHead>
                <TableHead>Ticket category</TableHead>
                <TableHead>Unmatched leads go to</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Launched</TableHead>
                <TableHead className="w-16">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isCreating && (
                <TableRow>
                  <TableCell>
                    <Input
                      autoFocus
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Big Pink Book"
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setIsCreating(false);
                          setNewName("");
                          setNewSlug("");
                          setCreateError(null);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={newSlug}
                      onChange={(e) => setNewSlug(e.target.value)}
                      placeholder="big-pink-book"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCreate();
                        if (e.key === "Escape") {
                          setIsCreating(false);
                          setNewName("");
                          setNewSlug("");
                          setCreateError(null);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell colSpan={TABLE_COLUMN_COUNT - 2}>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={handleCreate}
                        disabled={
                          !newName.trim() || !newSlug.trim() || isSaving
                        }
                      >
                        Add
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsCreating(false);
                          setNewName("");
                          setNewSlug("");
                          setCreateError(null);
                        }}
                      >
                        Cancel
                      </Button>
                      {createError && (
                        <span className="text-xs text-red-600">
                          {createError}
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {sources.length === 0 && !isCreating ? (
                <TableRow>
                  <TableCell
                    colSpan={TABLE_COLUMN_COUNT}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No landing pages set up yet.
                  </TableCell>
                </TableRow>
              ) : (
                sources.map((source) => (
                  <TableRow key={source.id}>
                    <TableCell className="font-medium text-foreground">
                      {editingId === source.id ? (
                        <Input
                          autoFocus
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveEdit();
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                      ) : (
                        <Link
                          to={paths.marketingLandingPageSubmissions({
                            leadSourceId: source.id,
                          })}
                          className="underline underline-offset-4 decoration-border hover:decoration-foreground"
                        >
                          {source.name}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {source.slug}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={source.is_active}
                          onCheckedChange={() => handleToggleActive(source)}
                          aria-label={
                            source.is_active
                              ? `Deactivate ${source.name}`
                              : `Activate ${source.name}`
                          }
                        />
                        <Badge
                          variant={source.is_active ? "success" : "neutral"}
                        >
                          {source.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Popover
                        open={categoriesPopoverId === source.id}
                        onOpenChange={(open) =>
                          handleOpenCategoriesPopover(source, open)
                        }
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex flex-wrap gap-1 max-w-48 text-left hover:opacity-80"
                            aria-label={`Edit product categories for ${source.name}`}
                          >
                            {source.marketing_categories?.length ? (
                              source.marketing_categories.map((cat) => (
                                <Badge key={cat} variant="secondary">
                                  {SELECTABLE_MARKETING_CATEGORIES.find(
                                    (c) => c.value === cat,
                                  )?.label ?? cat}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                Unscoped
                              </span>
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64" align="start">
                          <div className="text-xs text-muted-foreground mb-2">
                            Scope commercial-outcomes counts to these product
                            categories. Leave empty to count every order.
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {SELECTABLE_MARKETING_CATEGORIES.map((cat) => {
                              const isSelected = pendingCategories.includes(
                                cat.value,
                              );
                              return (
                                <Toggle
                                  key={cat.value}
                                  variant="outline"
                                  size="sm"
                                  pressed={isSelected}
                                  onPressedChange={(pressed) =>
                                    setPendingCategories((prev) =>
                                      pressed
                                        ? [...prev, cat.value]
                                        : prev.filter((c) => c !== cat.value),
                                    )
                                  }
                                  className="text-xs px-2"
                                >
                                  {cat.label}
                                </Toggle>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                    <TableCell>
                      <Popover
                        open={ticketCategoryPopoverId === source.id}
                        onOpenChange={(open) =>
                          setTicketCategoryPopoverId(open ? source.id : null)
                        }
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="text-left hover:opacity-80"
                            aria-label={`Set ticket category for ${source.name}`}
                          >
                            {source.ticket_category_id ? (
                              <Badge variant="secondary">
                                {ticketCategories.find(
                                  (c) => c.id === source.ticket_category_id,
                                )?.name ?? "Unknown category"}
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                Uncategorised
                              </span>
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72" align="start">
                          <div className="text-xs text-muted-foreground mb-2">
                            Which CRM category tickets from this page are filed
                            under. Leaving it unset still routes the ticket to a
                            rep — it just arrives uncategorised, with nothing on
                            it to say why.
                          </div>
                          {ticketCategories.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              No active ticket categories exist yet. Create one
                              under Settings first.
                            </p>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {ticketCategories.map((cat) => (
                                <Button
                                  key={cat.id}
                                  type="button"
                                  variant={
                                    source.ticket_category_id === cat.id
                                      ? "secondary"
                                      : "ghost"
                                  }
                                  size="sm"
                                  className="justify-start"
                                  onClick={() =>
                                    handleSetTicketCategory(source, cat.id)
                                  }
                                >
                                  {cat.name}
                                </Button>
                              ))}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="justify-start text-muted-foreground"
                                onClick={() =>
                                  handleSetTicketCategory(source, undefined)
                                }
                              >
                                Uncategorised
                              </Button>
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                    <TableCell>
                      <Popover
                        open={defaultRepPopoverId === source.id}
                        onOpenChange={(open) =>
                          setDefaultRepPopoverId(open ? source.id : null)
                        }
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="text-left hover:opacity-80"
                            aria-label={`Set who unmatched leads go to for ${source.name}`}
                          >
                            {source.default_sales_rep_user_id ? (
                              <Badge variant="secondary">
                                {salesReps.find(
                                  (u) =>
                                    u.id === source.default_sales_rep_user_id,
                                )?.name ?? "Unknown user"}
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                Nobody
                              </span>
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72" align="start">
                          <div className="text-xs text-muted-foreground mb-2">
                            Who picks up an enquiry when we can't work out whose
                            customer it is — someone new, or an account with no
                            rep. Enquiries from a known customer still go to
                            that customer's own rep.
                          </div>
                          {salesReps.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              No sales reps have signed in yet, so there is
                              nobody to assign to.
                            </p>
                          ) : (
                            <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
                              {salesReps.map((rep) => (
                                <Button
                                  key={rep.id}
                                  type="button"
                                  variant={
                                    source.default_sales_rep_user_id === rep.id
                                      ? "secondary"
                                      : "ghost"
                                  }
                                  size="sm"
                                  className="justify-start"
                                  onClick={() =>
                                    handleSetDefaultRep(source, rep.id)
                                  }
                                >
                                  {rep.name}
                                </Button>
                              ))}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="justify-start text-muted-foreground"
                                onClick={() =>
                                  handleSetDefaultRep(source, undefined)
                                }
                              >
                                Nobody — leave unassigned
                              </Button>
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateOrDash(source.created_at)}
                    </TableCell>
                    <TableCell>
                      <Popover
                        open={launchPopoverId === source.id}
                        onOpenChange={(open) =>
                          setLaunchPopoverId(open ? source.id : null)
                        }
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex items-center gap-1.5 text-sm hover:opacity-80"
                            aria-label={`Set launch date for ${source.name}`}
                          >
                            <CalendarIcon className="size-3.5 text-muted-foreground" />
                            {source.launched_at ? (
                              <span className="text-muted-foreground">
                                {formatDateOrDash(source.launched_at)}
                              </span>
                            ) : (
                              <Badge variant="neutral">Not live yet</Badge>
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={
                              source.launched_at
                                ? new Date(source.launched_at)
                                : undefined
                            }
                            onSelect={(date) =>
                              handleSetLaunchedAt(source, date)
                            }
                          />
                          {source.launched_at && (
                            <div className="border-t border-border p-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="w-full"
                                onClick={() =>
                                  handleSetLaunchedAt(source, undefined)
                                }
                              >
                                Mark as not live yet
                              </Button>
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                    <TableCell>
                      {editingId === source.id ? (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleSaveEdit}
                            disabled={!editingName.trim() || isSaving}
                          >
                            <Check className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingId(null)}
                          >
                            <X className="size-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Edit ${source.name}`}
                          onClick={() => handleStartEdit(source)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
